import type { Measure, WeekPoint } from "./margin_weeks";

/** The only numeric conversion in this file sizes the line: a layout number, never a figure shown as fact (as OrderChart). */
const n = (value: string): number => Number(value);

/** A row's measure over the window: a gap where a week has no point or its value is null, the last point marked. */
export function Sparkline({ points, weeks, measure, label }: { points: WeekPoint[]; weeks: string[]; measure: Measure; label: string }) {
  const W = 120, H = 28;
  const byWeek = new Map(points.map((p) => [p.iso_week, p] as const));
  const values = weeks.map((w) => byWeek.get(w)?.[measure] ?? null);
  const present = values.filter((v): v is string => v != null).map(n);
  if (present.length === 0) return <svg role="img" aria-label={`${label}: no points`} viewBox={`0 0 ${W} ${H}`} className="h-7 w-[120px]"><text x="0" y="18" fontSize="10" fill="var(--color-ks-grey-1)">no points</text></svg>;
  let min = Math.min(...present), max = Math.max(...present);
  if (max - min < 2) { max += 1; min -= 1; }
  const x = (i: number) => (weeks.length === 1 ? W / 2 : 2 + (i * (W - 4)) / (weeks.length - 1));
  const y = (v: number) => H - 3 - ((v - min) / (max - min)) * (H - 6);
  const segments: string[] = [];
  let current = "", lastIndex = -1;
  values.forEach((v, i) => {
    if (v == null) { if (current) segments.push(current); current = ""; return; }
    current += `${current ? " L" : "M"}${x(i)} ${y(n(v))}`;
    lastIndex = i;
  });
  if (current) segments.push(current);
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${W} ${H}`} className="h-7 w-[120px] shrink-0">
      {values.map((v, i) => (v == null ? <rect key={i} x={x(i) - 1.5} y="2" width="3" height={H - 4} fill="var(--color-ks-warn)" opacity="0.18" /> : null))}
      {segments.map((d, i) => <path key={i} d={d} fill="none" stroke="var(--color-ks-purple-2)" strokeWidth="1.6" />)}
      {lastIndex >= 0 && <circle cx={x(lastIndex)} cy={y(n(values[lastIndex] as string))} r="2.5" fill="var(--color-ks-purple)" />}
    </svg>
  );
}
